import { breakpoints } from '../constants';
import { chunk, requestNextAnimationFrame } from '../utility';

export default class paginatedGrid {

  constructor(el) {
    this.itemsContainer = el.querySelector('[data-paginated-grid-list]');
    this.pagesNav = el.querySelector('[data-paginated-grid-nav]');
    this.items = [...el.querySelectorAll('[data-paginated-grid-item]')];
    this.boundTransitionEndRemoveHandler = e => this._transitionEndRemoveHandler(e);

    this.breakpointPageSizeMap = new Map([
      ['break-0', 6],
      ['break-1', 16],
      ['break-2', 28],
    ]);

    this.state = {
      currentPage: 0,
      breakpoint: this._findCurrentBreakpoint(),
      transitionedOutCount: 0,
      prevPageSize: 0,
    };

    this.pages = chunk(this.items, this.breakpointPageSizeMap.get(this.state.breakpoint));
    this._breakpointListeners();
    this.setPage(this.state.currentPage);
  }

  /**
   * Updates this.state with the current page index
   * Triggers a render
   */
  setPage(currentPage) {
    this._updateState({ currentPage: currentPage < this.pages.length - 1 ? currentPage : this.pages.length - 1});
  }

  /**
   * Updates this.state with the current breakpoint
   * Triggers a render
   */
  setBreakpoint(breakpoint) {
    this._updateState({ breakpoint });
  }

  /**
   * Takes any updates passed to it as an arguement,
   * updates this.state, and passes both the new and old states to the _render() function.
   *   a. If the states are identical, simply remove all elements from DOM that shouldn't be there.
   *      This occurs on first render, when Wordpress has added all elements to the page (extras are hitten with CSS)
   *   b. If the states differ, call then _render function.
   * @param  {Object} updates Any updates to the state
   */
  _updateState(updates) {
    const prevState = {...this.state};
    this.state = {...prevState, ...updates};
    this.pages = chunk(this.items, this.breakpointPageSizeMap.get(this.state.breakpoint));
    this.state.currentPage = this.state.currentPage < this.pages.length - 1 ? this.state.currentPage : this.pages.length - 1;

    this._renderNavigation();
    if (JSON.stringify(this.state) !== JSON.stringify(prevState)) {
      this._render(this.state, prevState);
    } else {
      // states are the same, but an update has been requested.
      // simply remove all other items from other pages.
      this.items.forEach(el => {
        if (this.pages[this.state.currentPage].indexOf(el) === -1) el.remove();
      });
    }
  }

  /**
   * Renders the new page of items in the DOM.
   * Steps:
   *  - update this.state.prevPageSize.
   *    This is used to keep track of how many items have transitioned out.
   *    i.e. when are we clear to render the new page?
   *  - For each item on the current page, add an eventListner which will remove the element from the DOM
   *  - add a transition property, and set the opacity to 0 (initial transition state)
   *  - The eventListener will fire for each of the items as they fade out,
   *  - Once all of the items have transitioned out, the new page will be added (called from this._transitionEndRemoveHandler)
   *  If the states differ, but only by their breakpoint,
   *    we don't want to trigger an animation. We simple call the _transitionEndRemoveHandler directly.
   * @param  {Object} newState  The new state to render
   * @param  {Object} prevState The previous state that is still visible
   */
  _render(newState, prevState) {
    const prevPages = chunk(this.items, this.breakpointPageSizeMap.get(prevState.breakpoint));
    const prevPage = prevPages[prevState.currentPage];
    if (newState.breakpoint === prevState.breakpoint) {
      this.animateOutPage(prevPage);
    } else {
      prevPage.forEach(target => this._transitionEndRemoveHandler({ target }));
    }
  }

  animateOutPage(page) {
    this.state.prevPageSize = page.length;
    page.forEach(target => {
      target.addEventListener('transitionend', this.boundTransitionEndRemoveHandler);
      Object.assign(target.style, {
        transition: 'opacity ease 300ms',
        opacity: 0,
      });
    });
  }

  /**
   * Takes the current page according to the current state,
   * appends each item in that page to the DOM,
   * and triggers a fadeIn transition.
   */
  animateInPage(page) {
    page.forEach(el => {
      this.itemsContainer.append(el);
      Object.assign(el.style, {
        transition: 'opacity ease 300ms',
        opacity: 0,
      });
    });

    requestNextAnimationFrame(() =>
      page.forEach(el => el.style.opacity = '1'));
  }

  /**
   * Cleans up an element and removes it from the DOM after and event (intended for `transitionend`)
   * - remove style properties added for the transition
   * - remove element from the DOM
   * - calls animateInPage() if all items have been removed (according to this.state.transitionedOutCount)
   * @param  {HTMLElement} options.target  The HTMLElement to be removed from the DOM.
   */
  _transitionEndRemoveHandler({ target }) {
    target.removeEventListener('transitionend', this.boundTransitionEndRemoveHandler);
    this.state.transitionedOutCount++;
    target.style.transition = '';
    target.style.opacity = '';
    target.remove();
    if (this.state.transitionedOutCount === this.state.prevPageSize) {
      this.animateInPage(this.pages[this.state.currentPage]);
      this.state.transitionedOutCount = 0;
    }
  }

  /**
   * Simple function to re-render the current page,
   * no animations.
   * Don't add anything ot the DOM if there are 0-1 pages.
   */
  _renderNewPage() {
    if (this.pages.length <= 1) return;
    this.itemsContainer.innerHTML = '';
    this.itemsContainer.append(...this.pages[this.state.currentPage]);
  }

  /**
   * Simple function to re-render the navigation,
   * no animations.
   */
  _renderNavigation() {
    this.pagesNav.innerHTML = '';
    this.pagesNav.append(...this._createNavigation());
  }

  /**
   * For each breakpoint defined in this.breakpointPageSizeMap,
   * create a listener that will paginate the content,
   * set the current page (doesn't change, but could be based on the visible content),
   * and render the given page.
   * @return {void}
   */
  _breakpointListeners() {
    const mapKeys = [...this.breakpointPageSizeMap.keys()];
    for (let i = 0; i < this.breakpointPageSizeMap.size; i++) {
      window.matchMedia(`(min-width: ${breakpoints[mapKeys[i]]}px)`).addListener(e => {
        this.state.prevPageSize = this.pages[this.state.currentPage].length;
        this._updateState({
          breakpoint: e.matches ? mapKeys[i] : mapKeys[i - 1],
        });
      });
    }
  }

  /**
   * Creates a list of button HTML Elements corresponding to the number of pages of content.
   * When clicked, the button will activate that page.
   * @return {Array} array of button HTML Elements
   */
  _createNavigation() {
    const markup = [];
    if (this.pages.length <= 1) return [''];
    this.pages.forEach((page, i) => {
      const btn = document.createElement('button');
      btn.setAttribute('class', `pagination-dots__dot${i === this.state.currentPage ? ' pagination-dots__dot--active' : ''}`);
      btn.setAttribute('aria-label', `View Page ${i+1}`);
      btn.addEventListener('click', () => this.setPage(i));
      markup.push(btn);
    });
    return markup;
  }

  _findCurrentBreakpoint() {
    const mapKeys = [...this.breakpointPageSizeMap.keys()];
    for (let i = mapKeys.length - 1; i >= 0; i--) {
      if (window.matchMedia(`(min-width: ${breakpoints[mapKeys[i]]}px)`).matches) {
        return mapKeys[i];
      }
    }
  }

}
